import { useState, useEffect, useRef } from "react";
import * as React from "react";
import { Grid2 as Grid, Skeleton, Stack } from "@mui/material";
import {
  EPACompetencyRequirements,
  Mentors,
  QuickAccess,
  RecentFeedbackActivity,
  UpcomingTasks,
  WellnessDEIB,
  HomeFeedbackPeriodCard,
  Discussion,
  HospitalFeedbackCompetition,
} from "./components";
import { ROLES } from "types/user";
import { FeedbackOverview } from "./components/FeedbackOverview";
import { useHome } from "./hooks";
import { useUnansweredCampaigns } from "./hooks";
import { Modal } from "components/Modal";
import { CampaignAnswerModal } from "pages/Campaign/answer/CampaignAnswerModal";
import { useBadgeChecker } from "hooks/useBadgeChecker";
import { BadgeAnimationModal } from "components/Badge/BadgeAnimationModal";
import { Badge } from "types/badge";
import { useQuery } from "@apollo/client";
import { GET_USER_BADGES } from "graphql/badge/badge.query";

const DISCUSSION_ROLES = [
  ROLES.ADMIN,
  ROLES.COACH,
  ROLES.MANAGER,
  ROLES.DIRECTOR,
  ROLES.STUDENT,
];

const COMPETITION_ROLES = [
  ROLES.ADMIN,
  ROLES.COACH,
  ROLES.DIRECTOR,
  ROLES.MANAGER,
  ROLES.STUDENT,
];

const HomeSkeleton = ({ role }: { role?: string }) => {
  const isStudent = role === ROLES.STUDENT;
  const showsUpcomingTasks = isStudent || role === ROLES.COACH;
  const showsDiscussion = !!role && DISCUSSION_ROLES.includes(role as ROLES);

  return (
    <Grid
      container
      spacing={2}
      aria-label="Loading home page"
      data-testid="home-loading-skeleton"
    >
      <Grid size={{ md: 8, xs: 12 }}>
        <Stack spacing={2}>
          {showsUpcomingTasks && <Skeleton variant="rounded" height={144} />}
          {isStudent && <Skeleton variant="rounded" height={300} />}
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={300} />
          <Skeleton variant="rounded" height={280} />
        </Stack>
      </Grid>
      <Grid size={{ md: 4, xs: 12 }}>
        <Stack spacing={2}>
          {showsDiscussion && <Skeleton variant="rounded" height={240} />}
          {isStudent && (
            <>
              <Skeleton variant="rounded" height={160} />
              <Skeleton variant="rounded" height={220} />
              <Skeleton variant="rounded" height={300} />
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

const getDismissedCampaignStorageKey = (userId?: string) =>
  `dismissed-campaigns:${userId || "anonymous"}`;

const getDismissedCampaignIds = (userId?: string): string[] => {
  const storageKey = getDismissedCampaignStorageKey(userId);
  const rawValue = sessionStorage.getItem(storageKey);

  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const dismissCampaign = (campaignId: string, userId?: string) => {
  const storageKey = getDismissedCampaignStorageKey(userId);
  const dismissedCampaignIds = getDismissedCampaignIds(userId);

  if (!dismissedCampaignIds.includes(campaignId)) {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify([...dismissedCampaignIds, campaignId]),
    );
  }
};

export const Home = () => {
  const { data, loading, user, organization, statsData, dateRange, setDateRange } =
    useHome();
  const {
    unansweredCampaign,
    loading: campaignsLoading,
    refetchCampaigns,
  } = useUnansweredCampaigns();
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const { checkAndAwardBadge } = useBadgeChecker();
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const previousBadgeIdsRef = useRef<Set<string>>(new Set());
  const hasCheckedBadgesRef = useRef(false);

  // Fetch user badges to compare
  const { data: userBadgesData, refetch: refetchUserBadges } = useQuery(
    GET_USER_BADGES,
    {
      variables: { userId: user?.user_id || "" },
      skip:
        !user?.user_id ||
        (user?.role !== ROLES.COACH && user?.role !== ROLES.STUDENT),
    },
  );

  const isStudent = user?.role === ROLES.STUDENT;

  // Function to check for new badges by comparing current with previous
  const checkForNewBadges = React.useCallback(() => {
    if (!userBadgesData?.user_badge) return;

    const currentBadgeIds = new Set<string>(
      userBadgesData.user_badge.map((ub: any) => ub.badge_id as string),
    );

    // Find new badges that weren't in the previous set
    const newBadgeIds = Array.from(currentBadgeIds).filter(
      (id: string) => !previousBadgeIdsRef.current.has(id),
    );

    if (newBadgeIds.length > 0 && previousBadgeIdsRef.current.size > 0) {
      // Find all new badges to display
      const newBadgesData = userBadgesData.user_badge
        .filter((ub: any) => newBadgeIds.includes(ub.badge_id))
        .map((ub: any) => ub.badge)
        .filter(Boolean);
      if (newBadgesData.length > 0) {
        setNewBadges(newBadgesData);
        setShowBadgeModal(true);
      }
    }

    // Update the previous badge IDs
    previousBadgeIdsRef.current = currentBadgeIds;
  }, [userBadgesData]);

  // Check for new badges on home page load (for coaches and learners)
  useEffect(() => {
    if (
      !loading &&
      user?.user_id &&
      (user?.role === ROLES.COACH || user?.role === ROLES.STUDENT)
    ) {
      // First, check and award any eligible badges
      if (!hasCheckedBadgesRef.current) {
        hasCheckedBadgesRef.current = true;
        // Store initial badge IDs
        if (userBadgesData?.user_badge) {
          previousBadgeIdsRef.current = new Set(
            userBadgesData.user_badge.map((ub: any) => ub.badge_id),
          );
        }

        checkAndAwardBadge()
          .then((badges) => {
            if (badges && badges.length > 0) {
              // Refetch badges to get updated list, then show animation
              refetchUserBadges().then(() => {
                setNewBadges(Array.isArray(badges) ? badges : [badges]);
                setShowBadgeModal(true);
              });
            } else {
              // Check for new badges by comparing with previous
              setTimeout(() => {
                checkForNewBadges();
              }, 500); // Small delay to ensure badges are loaded
            }
          })
          .catch((error) => {
            console.error("Error checking badges on home load:", error);
            setTimeout(() => {
              checkForNewBadges();
            }, 500);
          });
      } else {
        // On subsequent loads, compare badges
        setTimeout(() => {
          checkForNewBadges();
        }, 500);
      }
    }
  }, [
    loading,
    user,
    checkAndAwardBadge,
    refetchUserBadges,
    userBadgesData,
    checkForNewBadges,
  ]);

  useEffect(() => {
    if (unansweredCampaign && !campaignsLoading) {
      const dismissedCampaignIds = getDismissedCampaignIds(user?.user_id);
      const campaignId = unansweredCampaign.campaign_id;
      const wasDismissed = campaignId
        ? dismissedCampaignIds.includes(campaignId)
        : false;

      setShowCampaignModal(!wasDismissed);
    } else if (!unansweredCampaign && !campaignsLoading) {
      setShowCampaignModal(false);
    }

    console.log("Unanswered Campaign:", unansweredCampaign);
  }, [unansweredCampaign, campaignsLoading]);

  const handleCampaignModalClose = () => {
    if (unansweredCampaign?.campaign_id) {
      dismissCampaign(unansweredCampaign.campaign_id, user?.user_id);
    }
    setShowCampaignModal(false);
  };

  const handleCampaignComplete = async () => {
    if (unansweredCampaign?.campaign_id) {
      dismissCampaign(unansweredCampaign.campaign_id, user?.user_id);
    }
    setShowCampaignModal(false);
    // Refetch campaigns instead of reloading the page
    if (refetchCampaigns) {
      await refetchCampaigns();
    }
  };

  if (loading) {
    return <HomeSkeleton role={user?.role} />;
  }
  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ md: 8, xs: 12 }}>
          <Stack spacing={2}>
            {user && (
              <HomeFeedbackPeriodCard
                value={dateRange}
                onChange={setDateRange}
              />
            )}
            {(user?.role === ROLES.STUDENT || user?.role === ROLES.COACH) && (
              <UpcomingTasks
                feedbacks={
                  data?.pendingFeedbacks?.length
                    ? data.pendingFeedbacks
                    : data?.feedbacks || []
                }
                user={user}
              />
            )}
            {user?.role === ROLES.STUDENT && (
              <EPACompetencyRequirements data={data} userRole={user?.role} />
            )}
            {!data && <Skeleton variant="rounded" height={300} />}
            {statsData && (
              <FeedbackOverview
                data={statsData}
                epaCompletionRate={data?.epaCompletionRate}
              />
            )}
            {data?.feedbacks && (
              <RecentFeedbackActivity
                feedbacks={data.feedbacks}
                user={user}
                organizationId={organization?.org_id}
                allRequirements={data.allRequirements || []}
              />
            )}
          </Stack>
        </Grid>
        <Grid size={{ md: 4, xs: 12 }}>
          <Stack spacing={2}>
            {user?.role && DISCUSSION_ROLES.includes(user.role) && (
              <Discussion />
            )}
            {user?.role && COMPETITION_ROLES.includes(user.role) && (
              <HospitalFeedbackCompetition
                orgId={organization?.org_id}
                dateRange={dateRange}
              />
            )}
            {isStudent && <Mentors data={data} />}
            {isStudent && <QuickAccess data={data} />}
            {/*<Calendar />*/}
            {isStudent && <WellnessDEIB />}
          </Stack>
        </Grid>
      </Grid>

      {unansweredCampaign && unansweredCampaign.campaign_id && (
        <Modal
          open={showCampaignModal}
          onClose={handleCampaignModalClose}
          title={
            unansweredCampaign.name
              ? `${unansweredCampaign.name} – Surveys`
              : "Campaign Surveys"
          }
          size="large"
        >
          <CampaignAnswerModal
            campaign={unansweredCampaign}
            onClose={handleCampaignModalClose}
            onComplete={handleCampaignComplete}
          />
        </Modal>
      )}

      <BadgeAnimationModal
        badges={newBadges}
        open={showBadgeModal}
        onClose={() => {
          setShowBadgeModal(false);
          setNewBadges([]);
        }}
      />
    </>
  );
};
